import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  Wrench,
  Disc,
  Gauge,
  BookOpen,
  HelpCircle,
  Building2,
  ChevronRight,
  User,
  LogIn,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { COMPANY } from "../lib/constants";
import OpenStatusBar from "./OpenStatusBar";
import { useAuth } from "../lib/AuthContext";

const NAV_LINKS = [
  { to: "/servicos", label: "Todos os Serviços", icon: Wrench },
  { to: "/pneus", label: "Pneus", icon: Disc },
  { to: "/simulador", label: "Simulador", icon: Gauge },
  { to: "/guias", label: "Guias", icon: BookOpen },
  { to: "/faq", label: "FAQ", icon: HelpCircle },
  { to: "/sobre", label: "Sobre Nós", icon: Building2 },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, admin, unreadCount } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `relative px-2.5 lg:px-3 py-2 rounded-full text-[11px] lg:text-xs uppercase tracking-[0.08em] font-display font-semibold transition-colors whitespace-nowrap ${
      isActive ? "text-[#FF5A00] bg-[#FF5A00]/10" : "text-white/80 hover:text-[#FF5A00] hover:bg-white/5"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || location.pathname !== "/" ? "glass-nav border-b border-[#2F568F]" : "bg-transparent"
      }`}
    >
      <OpenStatusBar />
      <div className="w-full px-6 md:px-6 lg:px-12 xl:px-20 flex items-center justify-between gap-4 h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
          aria-label="Global Pneus — início"
        >
          <div className="flex flex-col items-center gap-0.5">
            <img
              src={COMPANY.logo}
              alt="Global Pneus"
              className="h-10 w-10 md:h-12 md:w-12 object-contain transition-transform group-hover:scale-105"
            />
            <img
              src="/logo-confortauto-nav.png"
              alt="Membro ConfortAuto"
              className="h-4 md:h-5 object-contain opacity-60 group-hover:opacity-90 transition-opacity"
            />
          </div>
          <span className="hidden sm:block md:hidden xl:block font-display font-bold text-lg md:text-xl uppercase tracking-wide leading-tight whitespace-nowrap bg-gradient-to-r from-[#FF5A00] via-[#FF8534] to-[#FFD4B0] bg-clip-text text-transparent">
            {COMPANY.name}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-0.5 lg:gap-1 border border-[#2F568F]/50 rounded-full px-1.5 py-1 bg-[#0B1D44]/40">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "")}`}
              className={linkClass}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 lg:gap-3 shrink-0">
          {admin ? (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="inline-flex items-center gap-2 border border-[#5FA8FF]/60 text-[#5FA8FF] hover:bg-[#5FA8FF]/10 font-display font-semibold uppercase text-xs tracking-wider px-3 py-2 rounded-full transition-all"
            >
              <ShieldCheck className="w-4 h-4" />
              Admin
            </Link>
          ) : user ? (
            <>
              <Link
                to="/os-meus-orcamentos"
                data-testid="nav-my-quotes-link"
                className="relative inline-flex items-center gap-2 border-2 border-[#5FA8FF] bg-[#5FA8FF]/10 text-[#5FA8FF] hover:bg-[#5FA8FF] hover:text-[#0E2452] font-display font-bold uppercase text-xs tracking-wider px-4 py-2.5 rounded-full transition-all active:scale-95"
                style={{ boxShadow: "0 4px 14px rgba(95, 168, 255, 0.25)" }}
              >
                <FileText className="w-4 h-4" />
                <span className="hidden xl:inline">Os meus orçamentos</span>
                <span className="xl:hidden">Orçamentos</span>
                {unreadCount > 0 && (
                  <span
                    data-testid="nav-unread-badge"
                    className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 border-2 border-[#0A0A0A] text-white text-[10px] font-display font-bold flex items-center justify-center animate-pulse"
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Link>
              <Link
                to="/minha-conta"
                data-testid="nav-account-link"
                aria-label="A minha conta"
                title={user.name || "A minha conta"}
                className="inline-flex items-center justify-center w-10 h-10 border border-[#2F568F] text-white hover:border-[#FF5A00] hover:text-[#FF5A00] rounded-full transition-all"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-4 h-4" />
                )}
              </Link>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-link"
              className="inline-flex items-center gap-2 border-2 border-[#FF5A00] text-[#FF5A00] hover:bg-[#FF5A00] hover:text-white font-display font-bold uppercase text-xs tracking-widest px-4 py-2.5 rounded-full transition-all active:scale-95"
              style={{ boxShadow: "0 0 0 0 rgba(255, 90, 0, 0), 0 4px 14px rgba(255, 90, 0, 0.25)" }}
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden xl:inline">Entrar</span>
            </Link>
          )}
          <Link
            data-testid="nav-cta-quote"
            to="/orcamento"
            className="btn-primary text-xs xl:text-sm py-2.5 xl:py-3 px-4 xl:px-6 whitespace-nowrap"
          >
            <span className="xl:hidden">Orçamento</span>
            <span className="hidden xl:inline">Pedir Orçamento</span>
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[#2F568F] text-white hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden glass-nav border-t border-[#2F568F]">
          <div className="px-5 py-4 flex flex-col gap-0.5">
            {NAV_LINKS.map((l, idx) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.to.replace("/", "")}`}
                  style={{ animation: `mobileLinkIn 350ms ${idx * 50}ms backwards ease-out` }}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 py-2.5 border-b border-[#2F568F]/30 text-sm uppercase tracking-wider font-display font-medium transition-colors ${
                      isActive ? "text-[#FF5A00]" : "text-white/90 hover:text-[#FF5A00]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                          isActive
                            ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]"
                            : "border-[#2F568F] text-white/80 group-hover:border-[#FF5A00] group-hover:text-[#FF5A00]"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                      </span>
                      <span className="flex-1">{l.label}</span>
                      <ChevronRight
                        className={`w-3.5 h-3.5 transition-transform ${
                          isActive ? "text-[#FF5A00] translate-x-1" : "text-white/40 group-hover:text-[#FF5A00] group-hover:translate-x-1"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
            <div
              className="mt-4 grid grid-cols-2 gap-2"
              style={{ animation: `mobileLinkIn 350ms ${NAV_LINKS.length * 50}ms backwards ease-out` }}
            >
              <Link
                data-testid="mobile-cta-quote"
                to="/orcamento"
                className="col-span-2 inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E65100] text-white font-display font-bold uppercase text-[11px] tracking-wider py-2.5 rounded-full transition-all active:scale-[0.98]"
              >
                Pedir Orçamento
              </Link>
              {admin ? (
                <Link
                  data-testid="mobile-admin-link"
                  to="/admin"
                  className="col-span-2 inline-flex items-center justify-center gap-2 border border-[#5FA8FF]/60 text-[#5FA8FF] hover:bg-[#5FA8FF]/10 font-display font-semibold uppercase text-[11px] tracking-wider py-2.5 rounded-full transition-all"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Painel Admin
                </Link>
              ) : user ? (
                <>
                  <Link
                    data-testid="mobile-my-quotes-link"
                    to="/os-meus-orcamentos"
                    className="inline-flex items-center justify-center gap-1.5 border-2 border-[#5FA8FF] bg-[#5FA8FF]/10 text-[#5FA8FF] font-display font-bold uppercase text-[11px] tracking-wider py-2.5 rounded-full transition-all"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Orçamentos
                    {unreadCount > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[9px] font-display font-bold flex items-center justify-center">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    data-testid="mobile-account-link"
                    to="/minha-conta"
                    className="inline-flex items-center justify-center gap-1.5 border border-[#2F568F] text-white/85 font-display font-semibold uppercase text-[11px] tracking-wider py-2.5 rounded-full transition-all"
                  >
                    <User className="w-3.5 h-3.5" />
                    Conta
                  </Link>
                </>
              ) : (
                <Link
                  data-testid="mobile-login-link"
                  to="/login"
                  className="col-span-2 inline-flex items-center justify-center gap-2 border border-[#2F568F] text-white/80 hover:text-white font-display font-semibold uppercase text-[11px] tracking-wider py-2.5 rounded-full transition-all"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Entrar
                </Link>
              )}
            </div>
          </div>
          <style>{`
            @keyframes mobileLinkIn {
              from { opacity: 0; transform: translateX(-12px); }
              to   { opacity: 1; transform: translateX(0); }
            }
          `}</style>
        </div>
      )}
    </header>
  );
};

export default Navbar;
